import apiClient from '@/lib/apiClient'
import { LoginCredentials, User } from '@/types/api'
import React, { ReactNode, createContext, useContext, useEffect, useReducer } from 'react'
import { toast } from 'sonner'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void> // eslint-disable-line no-unused-vars
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void> // eslint-disable-line no-unused-vars
  clearError: () => void
  extendSession: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT_SUCCESS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' }

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      }
    case 'LOGOUT_SUCCESS':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      }
    default:
      return state
  }
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const sessionTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    // Set up automatic token refresh before expiry
    const setupAutoRefresh = () => {
      // Clear any existing timeout
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current)
      }

      const tokenExpiry = localStorage.getItem('admin_token_expiry')
      if (!tokenExpiry) return

      const expiryTime = new Date(tokenExpiry).getTime()
      const refreshTime = expiryTime - 2 * 60 * 1000 // 2 minutes before expiry
      const currentTime = Date.now()

      if (refreshTime > currentTime) {
        const timeout = refreshTime - currentTime
        sessionTimeoutRef.current = setTimeout(async () => {
          try {
            await apiClient.getCurrentUser()
            // After successful refresh, setup the next refresh
            setupAutoRefresh()
          } catch (error) {
            console.error('Auto refresh failed:', error)
            dispatch({ type: 'LOGOUT_SUCCESS' })
          }
        }, timeout)
      } else if (expiryTime > currentTime) {
        // If we're already within the 2-minute window, refresh immediately
        apiClient.getCurrentUser().catch(() => {
          dispatch({ type: 'LOGOUT_SUCCESS' })
        })
      }
    }

    if (state.isAuthenticated) {
      setupAutoRefresh()
    }

    return () => {
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current)
      }
    }
  }, [state.isAuthenticated])

  const extendSession = async () => {
    try {
      await apiClient.getCurrentUser()
      toast.success('Session extended')
    } catch {
      toast.error('Failed to extend session')
      dispatch({ type: 'LOGOUT_SUCCESS' })
    }
  }

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('admin_access_token')
      if (!token) {
        dispatch({ type: 'LOGOUT_SUCCESS' })
        return
      }

      // Check if token is expired
      const tokenExpiry = localStorage.getItem('admin_token_expiry')
      if (tokenExpiry) {
        const expiryTime = new Date(tokenExpiry).getTime()
        const currentTime = Date.now()
        if (expiryTime <= currentTime) {
          // Token is expired, try to get current user which will trigger refresh
          try {
            await apiClient.getCurrentUser()
          } catch {
            dispatch({ type: 'LOGOUT_SUCCESS' })
            return
          }
        }
      }

      const response = await apiClient.getCurrentUser()
      if (response.data) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      dispatch({ type: 'LOGOUT_SUCCESS' })
    }
  }

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'LOGIN_START' })
      const response = await apiClient.login(credentials)

      if (response.data?.user) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data.user })
        toast.success('Welcome back!')
      } else {
        throw new Error('Invalid response format')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed'
      dispatch({ type: 'LOGIN_FAILURE', payload: message })
      toast.error(message)
      throw error
    }
  }

  const logout = async () => {
    try {
      await apiClient.logout()
      dispatch({ type: 'LOGOUT_SUCCESS' })
      toast.success('Logged out successfully')
    } catch {
      // Even if logout fails on server, clear local state
      dispatch({ type: 'LOGOUT_SUCCESS' })
      toast.info('Logged out locally')
    }
  }

  const resetPassword = async (email: string) => {
    try {
      await apiClient.resetPassword(email)
      toast.success('Password reset email sent')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reset failed'
      toast.error(message)
      throw error
    }
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const refreshUser = async () => {
    try {
      const response = await apiClient.getCurrentUser()
      if (response.data) {
        dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
      }
    } catch {
      // If refresh fails, user might be logged out
      dispatch({ type: 'LOGOUT_SUCCESS' })
    }
  }

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    resetPassword,
    clearError,
    extendSession,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/* eslint-disable react-refresh/only-export-components */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
